import { useEffect, useRef, useState } from "react";
import { func, node } from "prop-types";
import { useSnackbar } from "notistack";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { applyPatchExtended, applyCommentStorePatch } from "@repo/structure";
import { useTranslation } from "react-i18next";
import {
  CoreCommands,
  EditorSocketContext,
  TemplateCommands,
  TemplateCommentCommands,
  TemplateEditorCommands
} from "@client";
import { templateEditor } from "@paths";
import { PROJECT_STATUS, SNACKBAR_WEBSOCKET_RESTORED } from "@utils";
import { useStores, useWorkspaceWebsocket } from "@hooks";
import { useEstimateComments, useEstimateEditorStore } from "@tools";
import { drawUserMovement } from "./utils/drawUserMovement";
import { createCustomWorkTypeQuery } from "@query";

// @todo: abstract some of the similar functions from EditorSocketProvider
export const TemplateSocketProvider = ({
  children,
  reloadTemplateData
}) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { userStore, stateStore } = useStores();
  const editorStore = useEstimateEditorStore();
  const commentStore = useEstimateComments();
  const {
    init,
    socket,
    connectionOnline,
    sendAction,
    requestTemplateRename,
    requestWorkTypeCreate,
    requestWorkTypeChange,
    setupWorkspaceEvents,
    requestCompanyLabelCreate,
    requestCompanyLabelRemove,
  } = useWorkspaceWebsocket();
  
  const [isSetup, setSetupStatus] = useState(false);
  const [users, setUsers] = useState([]);
  const [hasConnectionError, setConnectionError] = useState(false);

  const lastSentAction = useRef(null);
  const templateJoined = useRef(null);
  const connectionOnlineSnackbar = useRef(null);
  const connectionRestoredSnackbar = useRef(null);
  
  // --------- connections ---------
  
  const joinTemplate = (templateId, templateHash, versionKey) => {
   
    console.log("JOINING TEMPLATE", templateId)
    
    setupProjectEvents(
      true,
      true,
      Boolean(templateHash),
    );
    
    socket.current.emit(CoreCommands.JOIN_TEMPLATE, {
      templateId,
      companyId: userStore.companyId,
      versionKey,
      commentHash: templateHash,
      userId: userStore.data.id,
      userUuid: userStore.data.uuid,
      name: userStore.data.fullname,
      email: userStore.data.email,
      avatar: userStore.data.avatar,
    });
    templateJoined.current = { templateId, templateHash };
  };
  
  const closeEstimate = () => {
    if(!templateJoined.current)
      return;
    console.log("CLOSING TEMPLATE");
    setupProjectEvents(false);
    socket.current.emit(CoreCommands.CLOSE_TEMPLATE, {
      templateId: templateJoined.current.templateId,
      commentHash: templateJoined.current.templateHash,
      userId: userStore.data.id,
      userUuid: userStore.data.uuid,
    });
    templateJoined.current = null;
    
    lastSentAction.current = "END";
    closeSnackbar(connectionOnlineSnackbar.current);
    closeSnackbar(connectionRestoredSnackbar.current);
    setUsers([]);
  };
  
  const setupProjectEvents = (on, openEditionChannel, openCommentsChannel) => {
    if(!on) {
      socket.current.removeAllListeners();
      setupWorkspaceEvents(true);
      return;
    }
    
    socket.current.on(CoreCommands.ACTION_ERROR, onError);
    socket.current.on(TemplateEditorCommands.USER_JOINED, onJoined);
    socket.current.on(TemplateEditorCommands.USER_LEFT, onLeft);
    socket.current.on(CoreCommands.JOIN_TEMPLATE, onTemplateJoinError);
    socket.current.on(TemplateCommands.TEMPLATE_PROGRESS, onTemplateOpenProgress);
    
    if(openEditionChannel || !on) {
      socket.current.on(CoreCommands.WORKTYPE_ADD, onAddNewWorkType);
      socket.current.on(CoreCommands.WORKTYPE_EDIT, onWorkTypeChange);
      socket.current.on(CoreCommands.WORKTYPE_REMOVE, onWorkTypeRemove);
      
      socket.current.on(TemplateEditorCommands.USER_MOVE, drawUserMovement);
      socket.current.on(TemplateEditorCommands.PATCH, onStructurePatch);
      
      socket.current.on(TemplateEditorCommands.VERSION_LIST, onReceiveVersionList);
      socket.current.on(TemplateEditorCommands.VERSION_REQUEST, onReceiveVersion);
      socket.current.on(TemplateEditorCommands.VERSION_NEW, onCreateVersion);
      socket.current.on(TemplateEditorCommands.VERSION_REMOVE, onRemoveVersion);
      socket.current.on(TemplateEditorCommands.VERSION_RENAME, onRenameVersion);
      socket.current.on(TemplateEditorCommands.VERSION_ORDER, onReorderVersion);
      socket.current.on(TemplateEditorCommands.VERSION_LOCK, onVersionLock);
      socket.current.on(TemplateEditorCommands.VERSION_VISIBILITY, onVersionVisibility);
      socket.current.on(TemplateEditorCommands.VERSIONS_WORK_TYPE_UPDATE, onVersionWorkTypesUpdate);
      
      socket.current.on(TemplateCommands.TEMPLATE_METADATA, onTemplateMetadataUpdate);
      
      socket.current.on(TemplateCommands.TEMPLATE_RENAME, onTemplateRename);
      socket.current.on(TemplateCommands.TEMPLATE_REMOVE, onTemplateRemove);
    }
    if(openCommentsChannel || !on) {
      socket.current.on(TemplateCommentCommands.COMMENT_PATCH, onCommentPatch);
    }
    
    setSetupStatus(true);
  }

  const resetProjectData = async () => {
    if(!templateJoined.current) return;
    const { hash, estimateData } = await reloadTemplateData();
    socket.current.emit(CoreCommands.JOIN_TEMPLATE, {
      templateId: estimateData.id,
      companyId: userStore.companyId,
      versionKey: editorStore.currentVersionKey,
      commentHash: hash,
      userId: userStore.data.id,
      userUuid: userStore.data.uuid,
      name: userStore.data.fullname,
      email: userStore.data.email,
      avatar: userStore.data.avatar,
    });
    templateJoined.current = { templateId: estimateData.id, templateHash: hash };
  }
  
  useEffect(() => {
    if(isSetup && connectionOnline) {
      onReconnect();
    }
    
    if(!connectionOnline) {
      onDisconnect();
    }
  }, [connectionOnline]);

  const onReconnect = async () => {
    console.log("EDITOR RECONNECTED");
   
    if(templateJoined.current) {
      await resetProjectData();
      closeSnackbar(connectionOnlineSnackbar.current);
      connectionRestoredSnackbar.current = enqueueSnackbar(
        t("alerts.editor.connection_restored"),
        {
          key: SNACKBAR_WEBSOCKET_RESTORED,
          variant: "success",
          autoHideDuration: 2000
        }
      );
    }
  };

  const onDisconnect = () => {
    console.log("EDITOR DISCONNECTED");
    if(lastSentAction.current !== "END") {
      closeSnackbar(connectionRestoredSnackbar.current);
      connectionOnlineSnackbar.current = enqueueSnackbar(
        t("alerts.editor.connection_offline"),
        {
          persist: true,
          variant: "warning"
        }
      );
    }
    setUsers([]);
  };

  const madeIdenticalAction = (command, payload, additionalChecks) => {
    if (!lastSentAction.current) return false;
    return (
      lastSentAction.current.command === command &&
      lastSentAction.current.timestamp > payload.timestamp &&
      (!additionalChecks || additionalChecks(lastSentAction.current.payload))
    );
  };

  const saveAndSendAction = (command, payload) => {
    if (!socket.current?.connected) return;
    const timestamp = new Date().getTime();
    payload.timestamp = timestamp;
    payload.templateId = templateJoined.current.templateId;
    lastSentAction.current = { command, payload, timestamp };
    sendAction(command, payload);
  };
  
  const navigateAndScroll = (versionKey) => {
    navigate(templateEditor(templateJoined.current.templateId, versionKey), { replace: true });
      stateStore.scrollRef?.scrollTo({ top: editorStore.versionScroll[editorStore.currentVersionKey] || 0 });
  }

  // --------- remote actions ---------

  const onJoined = (userData) => {
    console.log("USER JOINED");
    setUsers((l) => [...l, ...userData])
  };

  const onLeft = (userSocketIds) => {
    console.log("USER LEFT");
    setUsers((l) => l.filter((u) => !userSocketIds.includes(u.socketId)));
  };
  
  const onTemplateJoinError = ({ status }) => {
    editorStore.setCreatedStatus(status);
  }
  
  const onTemplateOpenProgress = ({ progress, stepName }) => {
    stateStore.setProjectCreatorProgress(progress, stepName);
  }
  
  const onError = (data) => {
    console.error("ERROR => ", data.message);
    setConnectionError(true);
  }
  
  const onStructurePatch = (data) => {
    
    const structure = editorStore.estimateVersions.find(
      (v) => v.key === data.versionKey
    )?.structure;
    
    if (structure) try {
      structure.historyManager.withoutUndo(() => {
        data.patches.forEach(p => {
          if(p.removesElement && p.identifier)
          structure.historyManager.clearByTargetId(p.identifier)
        })
        applyPatchExtended(structure, data.patches);
      })
    } catch(e) {
      console.log("Error: applyPatch", e);
    }
  };

  const onReceiveVersionList = ({ message, versions, comments }) => {
    if(message)
      console.error(message);
    if(versions)
        editorStore.setVersions(versions);
    if(comments)
        commentStore.setGroups(comments);
  }
  
  const onReceiveVersion = ({ message, versionData }) => {
    if(message)
      console.error(message);
    if(versionData && versionData.structure) {
      editorStore.setVersionData(versionData, !userStore.isManager && !userStore.isBusinessUser);
      navigateAndScroll(versionData.key);
    }
    
    stateStore.resetProjectCreatorProgress();
  };

  const onCreateVersion = ({ versionData, userSocketId, copiedComments }) => {
    editorStore.addNewVersion(versionData, userSocketId === socket.current.id);
    
    if(copiedComments?.length)
      commentStore.addGroups(copiedComments);
    navigateAndScroll(versionData.key);
  };

  const onRemoveVersion = ({ versionKey, newVersionData, versionName, userSocketId }) => {
    if(newVersionData && editorStore.currentVersion === versionKey) {
      editorStore.setVersionData(newVersionData, !userStore.isManager && !userStore.isBusinessUser);
      navigateAndScroll(newVersionData.key);
      if(socket.current.id !== userSocketId)
        enqueueSnackbar(
          <p>
            {t("common.version")}
            <b>{` ${versionName} `}</b>
            {t("alerts.editor.version_removed")}
          </p>,
          { variant: "warning", autoHideDuration: 2000 }
        );
    }
    editorStore.removeSelectedVersion(versionKey);
  };

  const onRenameVersion = (data) => {
    if (
      madeIdenticalAction(
        TemplateEditorCommands.VERSION_RENAME,
        data,
        (p) => p.versionKey === data.versionKey
      )
    )
      return;
    const { versionKey, newName } = data;
    editorStore.editVersionName(versionKey, newName);
  };

  const onReorderVersion = (data) => {
    if (madeIdenticalAction(TemplateEditorCommands.VERSION_ORDER, data))
      return;
    editorStore.updateVersionOrder(data.newOrder);
  };
  
  const onVersionLock = (data) => {
    editorStore.setEditionLock(data.versionKey, data.locked);
  }
  
  const onVersionVisibility = (data) => {
    editorStore.setVersionHidden(data.versionKey, data.visible);
  }
  
  const onVersionWorkTypesUpdate = ({ workTypeIds }) => {
    editorStore.updateCustomWorkTypeIds(workTypeIds);
    editorStore.currentEstimateStructure?.historyManager.withoutUndo(() => {
      editorStore.currentEstimateStructure.setWorkTypeIdsFromDB(workTypeIds);
    })
  }
  
  const onAddNewWorkType = ({ id, name, backgroundColor }) => {
    editorStore.addCustomWorkType(id, name, backgroundColor);
  };
  
  const onWorkTypeChange = ({ id, name, backgroundColor }) => {
    editorStore.updateWorkTypeData(id, name, backgroundColor);
  };
  
  const onWorkTypeRemove = ({ id }) => {
    editorStore.removeWorkType(id);
  };
  
  const onTemplateMetadataUpdate = ({ metadata, workTypesCreated }) => {
    if(metadata?.workTypes.length)
      editorStore.addCustomEstimateWorkTypes(metadata.workTypes, workTypesCreated);
  }

  const onTemplateRename = (data) => {
    if (madeIdenticalAction(TemplateCommands.TEMPLATE_RENAME, data)) return;
    editorStore.setEstimateName(data.newName);
  };
  
  const onTemplateRemove = ({ templateId }) => {
    if(templateId !== templateJoined.current.templateId)
      return;
    editorStore.setEstimateStatus(PROJECT_STATUS.REMOVED);
  }
  
  const onCommentPatch = (data) => {
    try {
      commentStore.historyManager.withoutUndo(() => {
        applyCommentStorePatch(commentStore, data.patches);
      });
    } catch(e) {
      console.log("Error: onCommentPatch", e);
    }
  }

  // --------- USER ACTIONS ---------

  const sendMoveData = (
    clientX,
    clientY,
    elementType,
    elementPath,
    elementId
  ) => {
    sendAction(TemplateEditorCommands.USER_MOVE, {
      id: userStore.data.id,
      vW: window.innerWidth,
      // vH: containerHeight,
      x: clientX,
      y: clientY,
      elementType,
      elementPath,
      elementId,
    });
  };
  
  const requestStructurePatch = (patches, metadata) => {
    saveAndSendAction(TemplateEditorCommands.PATCH, {
      versionKey: editorStore.currentVersionKey,
      timestamp: new Date().getTime(),
      patches,
      metadata,
    });
  };
  
  const requestCommentPatch = (patches) => {
    saveAndSendAction(TemplateCommentCommands.COMMENT_PATCH, {
      versionKey: editorStore.currentVersionKey,
      timestamp: new Date().getTime(),
      patches,
    });
  };

  const requestExistingVersion = (versionKey) => {
    saveAndSendAction(TemplateEditorCommands.VERSION_REQUEST, {
      templateId: templateJoined.current.templateId,
      userUuid: userStore.userUuid,
      versionKey,
    });
  };
  
  const requestNewVersion = (copyComments) => {
    saveAndSendAction(TemplateEditorCommands.VERSION_NEW, {
      originVersionKey: editorStore.currentVersionKey,
      copyComments,
    });
  };
  
  const requestNewImportedVersion = (structure, workTypes) => {
    saveAndSendAction(TemplateEditorCommands.VERSION_IMPORT, { structure, workTypes });
  };

  const requestVersionRename = (versionKey, newName) => {
    saveAndSendAction(TemplateEditorCommands.VERSION_RENAME, { versionKey, newName });
  };

  const requestVersionRemoval = (versionKey) => {
    saveAndSendAction(TemplateEditorCommands.VERSION_REMOVE, { versionKey });
  };

  const requestVersionVisibility = (versionKey, visible) => {
    saveAndSendAction(TemplateEditorCommands.VERSION_VISIBILITY, { versionKey, visible });
  };

  const requestVersionLock = (versionKey, locked) => {
    saveAndSendAction(TemplateEditorCommands.VERSION_LOCK, { versionKey, locked });
  };

  const requestVersionReorder = (movedVersionKey, newOrder) => {
    saveAndSendAction(TemplateEditorCommands.VERSION_ORDER, { movedVersionKey, newOrder });
  };
  
  const requestCommentAdd = async (commenterData, path, name, body, mentions, internal, parentUuid) => {
    const versionKey = editorStore.currentVersionKey;
    const uuid = window.crypto.randomUUID();
    const createdAt = format(new Date(), "yyyy-MM-dd HH:mm:ss")
    
    saveAndSendAction(TemplateCommentCommands.COMMENT_ADD, {
      versionKey,
      commentData: {
        uuid,
        createdAt,
        path, name, version: versionKey,
        body, mentions, parentUuid,
        user: commenterData,
        internal: internal ? 1 : 0
      }
    });
    return {
      uuid,
      parentUuid,
      body,
      createdAt,
      user: commenterData,
      isRead: true,
    }
  };
  
  const requestCommentEdit = async (commentUuid, commenterUuid, version, body, mentions, parentUuid) => {
    const versionKey = editorStore.currentVersionKey;
    saveAndSendAction(TemplateCommentCommands.COMMENT_EDIT, {
      versionKey, commentUuid, commenterUuid, version, body, parentUuid, mentions
    });
  };
  
  const requestCommentRemove = async (commentUuid, commenterUuid, parentUuid, path) => {
    const versionKey = editorStore.currentVersionKey;
    if(parentUuid)
      saveAndSendAction(TemplateCommentCommands.COMMENT_REMOVE, {
        versionKey, commentUuid, parentUuid, commenterUuid,
      });
    else if(path)
      saveAndSendAction(TemplateCommentCommands.COMMENT_REMOVE_PATH, { versionKey, path });
  };
  
  const requestCommentPathRemove = async (path) => {
    const versionKey = editorStore.currentVersionKey;
    saveAndSendAction(TemplateCommentCommands.COMMENT_REMOVE_PATH, { versionKey, path });
  };
  
  const requestCommentMove = async (oldPath, path) => {
    const versionKey = editorStore.currentVersionKey;
    saveAndSendAction(TemplateCommentCommands.COMMENT_MOVE, { versionKey, oldPath, path });
  };
  
  const requestCommentPathElementRename = async (path, newName) => {
    const versionKey = editorStore.currentVersionKey;
    saveAndSendAction(TemplateCommentCommands.COMMENT_ELEMENT_RENAME, { versionKey, newName, path });
  };
  
  const requestVersionWorkTypesChange = async (updatedWorkTypes) => {
    for(let wT of updatedWorkTypes) {
      const t = await createCustomWorkTypeQuery(wT.name, wT.color);
      wT.newId = t.id;
      requestWorkTypeCreate(t);
    }
    
    const workTypeIds = updatedWorkTypes
      .filter(wT => wT.oldId && wT.newId)
      .map(({ oldId, newId }) => ({ oldId, newId }))
    onVersionWorkTypesUpdate({ workTypeIds });
    saveAndSendAction(TemplateEditorCommands.VERSIONS_WORK_TYPE_UPDATE, { workTypeIds });
  }
  
  const requestEstimateLabelCreate = (labelData) => {
    saveAndSendAction(TemplateEditorCommands.LABEL_CREATE, {
      projectUuid: templateJoined.current.projectUuid,
      labelId: labelData.id,
      labelData
    });
  }
  
  const requestEstimateLabelRemove = (labelSystemId) => {
    saveAndSendAction(TemplateEditorCommands.LABEL_REMOVE, {
      projectUuid: templateJoined.current.projectUuid,
      labelId: labelSystemId,
    });
  }
  
  const requestLabelUpdate = (labelSystemId, labelData, isProjectLabel) => {
    saveAndSendAction(CoreCommands.LABEL_UPDATE, {
      projectUuid: templateJoined.current.projectUuid,
      labelId: labelSystemId,
      labelData,
      isProjectLabel,
    });
  }
  
  const value = {
    users,
    hasConnectionError,
    editionPrivileges: true,
    initSocket: init,
    joinEstimate: joinTemplate,
    closeEstimate,
    
    sendMoveData,
    
    requestEstimateRename: requestTemplateRename,
    
    requestStructurePatch,
    requestCommentPatch,
    
    requestExistingVersion,
    requestNewVersion,
    requestNewImportedVersion,
    requestVersionRename,
    requestVersionRemoval,
    requestVersionReorder,
    requestVersionVisibility,
    requestVersionLock,
    
    requestCommentAdd,
    requestCommentEdit,
    requestCommentRemove,
    requestCommentPathRemove,
    requestCommentMove,
    requestCommentPathElementRename,
    
    requestWorkTypeChange,
    
    requestVersionWorkTypesChange,
    requestEstimateLabelCreate,
    requestEstimateLabelRemove,
    requestLabelUpdate,
    requestCompanyLabelCreate,
    requestCompanyLabelRemove,
  };

  return (
    <EditorSocketContext.Provider value={value}>
      {children}
    </EditorSocketContext.Provider>
  );
};

TemplateSocketProvider.propTypes = {
  children: node.isRequired,
  reloadTemplateData: func,
};
